import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { MapPin, Plus, PencilSimple, Trash, CircleNotch } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TIMEZONES } from "@/components/admin/constants";

const LOCATION_TYPES = [
  ["headquarters", "Headquarters"],
  ["office", "Office"],
  ["region", "Region"],
];

async function loadLocationsInto(setLocations) {
  try {
    const { data } = await api.get("/locations");
    setLocations(data);
  } catch (e) {
    toast.error("Locations could not be loaded.");
  }
}

function LocationDialog({ location, onClose, onDone }) {
  const editing = Boolean(location);
  const [form, setForm] = useState({
    name: location?.name || "", location_type: location?.location_type || "office",
    address: location?.address || "", city: location?.city || "", state: location?.state || "",
    zip_code: location?.zip_code || "", region: location?.region || "",
    timezone: location?.timezone || "America/New_York",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = {
        ...form,
        address: form.address || null, city: form.city || null, state: form.state || null,
        zip_code: form.zip_code || null, region: form.region || null,
      };
      if (editing) await api.patch(`/locations/${location.id}`, body);
      else await api.post("/locations", body);
      toast.success(editing ? "Location updated" : "Location created");
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-md" data-testid="location-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">
            {editing ? "Edit Location" : "New Location"}
          </DialogTitle>
          <DialogDescription>Company locations set base assignments and time zones for users.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Name *</Label>
            <Input required value={form.name} onChange={set("name")} className="rounded-sm" data-testid="location-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Type</Label>
              <Select value={form.location_type} onValueChange={set("location_type")}>
                <SelectTrigger className="rounded-sm" data-testid="location-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOCATION_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Time zone</Label>
              <Select value={form.timezone} onValueChange={set("timezone")}>
                <SelectTrigger className="rounded-sm" data-testid="location-timezone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Street address</Label>
            <Input value={form.address} onChange={set("address")} className="rounded-sm" data-testid="location-address" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">City</Label>
              <Input value={form.city} onChange={set("city")} className="rounded-sm" data-testid="location-city" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">State</Label>
              <Input value={form.state} onChange={set("state")} className="rounded-sm" data-testid="location-state" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">ZIP</Label>
              <Input value={form.zip_code} onChange={set("zip_code")} className="rounded-sm" data-testid="location-zip" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Region</Label>
            <Input value={form.region} onChange={set("region")} className="rounded-sm" placeholder="e.g. Southeast" data-testid="location-region" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="submit-location">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : editing ? "Save location" : "Create location"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function LocationsTab({ isGlobalAdmin }) {
  const [locations, setLocations] = useState(null);
  const [dialog, setDialog] = useState(null); // {location?} | null
  const helpers = useRef({ loadLocationsInto });
  const load = useCallback(() => helpers.current.loadLocationsInto(setLocations), [helpers, setLocations]);
  useEffect(() => { load(); }, [load]);

  const remove = async (loc) => {
    if (!window.confirm(`Delete location "${loc.name}"? Users based here must be reassigned first.`)) return;
    try {
      await api.delete(`/locations/${loc.id}`);
      toast.success("Location deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (locations === null) {
    return <div className="flex justify-center py-16"><CircleNotch size={24} className="animate-spin text-[#032852]" /></div>;
  }

  return (
    <div data-testid="locations-tab">
      {isGlobalAdmin && (
        <div className="mb-3 flex justify-end">
          <Button onClick={() => setDialog({})} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="new-location-btn">
            <Plus size={15} weight="bold" /> New location
          </Button>
        </div>
      )}
      <div className="overflow-hidden rounded-sm border border-[#E4E4E7] bg-white">
        {locations.map((loc) => (
          <div key={loc.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E4E4E7] px-5 py-4 last:border-0 hover:bg-[#FAFAFA]" data-testid={`location-row-${loc.id}`}>
            <div className="flex min-w-0 items-center gap-3">
              <MapPin size={20} className="shrink-0 text-[#032852]" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-[#09090B]">
                  {loc.name}
                  {loc.location_type === "headquarters" && (
                    <Badge className="rounded-sm border-0 bg-[#032852] text-[10px] font-bold uppercase text-white hover:bg-[#032852]">HQ</Badge>
                  )}
                </div>
                <p className="truncate text-xs text-[#6D6D70]">
                  {[loc.address, loc.city, loc.state, loc.zip_code].filter(Boolean).join(", ") || "No address"}
                  {loc.region ? ` · ${loc.region}` : ""} · {loc.timezone}
                </p>
              </div>
            </div>
            {isGlobalAdmin && (
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => setDialog({ location: loc })} data-testid={`edit-location-${loc.id}`}>
                  <PencilSimple size={14} /> Edit
                </Button>
                <Button size="sm" variant="outline" className="rounded-sm border-[#EF4444]/40 text-xs text-[#B91C1C] hover:bg-[#EF4444]/5" onClick={() => remove(loc)} data-testid={`delete-location-${loc.id}`}>
                  <Trash size={14} /> Delete
                </Button>
              </div>
            )}
          </div>
        ))}
        {locations.length === 0 && <p className="py-12 text-center text-sm text-[#6D6D70]">No locations yet.</p>}
      </div>
      {!isGlobalAdmin && (
        <p className="mt-2 text-xs text-[#6D6D70]">Locations are managed by the Global Administrator.</p>
      )}
      {dialog && (
        <LocationDialog key={dialog.location?.id || "new"} location={dialog.location}
          onClose={() => setDialog(null)} onDone={load} />
      )}
    </div>
  );
}
